import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { assert } from '@ember/debug';

import type IntlService from 'ember-intl/services/intl';

interface UiInputGroupNumberInputSignature {
  Element: HTMLInputElement;
  Args: {
    Named: {
      id: string;
      iconLeft?: string;
      hasError?: boolean;
      ariaDescribedby?: string;
      iconRight?: string;
      suffix?: string;
      initialValue?: number | string;
      inputmode?: 'decimal' | 'numeric';
      fractionDigits?: number;
      onIconRightClick?: () => void;
      onInput: (numString: string, event: Event) => void;
    };
  };
}

export default class UiInputGroupNumberInputComponent extends Component<UiInputGroupNumberInputSignature> {
  @service
  declare intl: IntlService;

  previousValue: string;
  previousFormattedValue: string;
  formattedValue: string;

  get inputmode() {
    return this.args.inputmode || 'decimal';
  }

  get ariaDescribedby() {
    const ids = [
      this.args.ariaDescribedby,
      this.args.suffix ? `${this.args.id}-suffix` : undefined,
    ].filter(Boolean);

    return ids.length ? ids.join(' ') : undefined;
  }

  get paddingRightClass() {
    if (this.args.suffix && this.args.iconRight) return 'pr-20';

    return this.args.suffix || this.args.iconRight ? 'pr-10' : '';
  }

  get formatterOptions() {
    if (this.inputmode === 'decimal') {
      return {
        minimumFractionDigits: this.args.fractionDigits ?? 2,
        maximumFractionDigits: this.args.fractionDigits ?? 2,
        useGrouping: false,
      };
    }

    if (this.inputmode === 'numeric') {
      return {
        maximumFractionDigits: 0,
        useGrouping: false,
      };
    }
  }

  parse(val: string) {
    let result;

    if (this.inputmode === 'decimal') {
      const fractionDigits = this.args.fractionDigits ?? 2;
      const regex = new RegExp(`(^\\d+[.]\\d{0,${fractionDigits}}).*`, 'g');

      result = val
        .replace(' ', '')
        .replace(',', '.')
        .replace(/[.](?=.*[.])/g, '')
        .replace(regex, '$1');
    }

    if (this.inputmode === 'numeric') {
      const normalizedValue = val.trim();

      result = /^\d+$/.test(normalizedValue) ? normalizedValue : '';
    }

    return result ? +result : NaN;
  }

  format(val: string) {
    if (val === '') return '';

    return this.intl.formatNumber(+val, this.formatterOptions);
  }

  constructor(
    owner: unknown,
    args: UiInputGroupNumberInputSignature['Args']['Named']
  ) {
    super(owner, args);

    const initialValue = `${args.initialValue ?? ''}`;

    this.previousValue = initialValue;
    this.previousFormattedValue = this.format(initialValue);

    this.formattedValue = this.format(initialValue);
  }

  @action
  onInput(event: Event) {
    assert(
      `Expected input event handler to be used on an element`,
      event.target instanceof HTMLInputElement
    );

    if (
      this.previousFormattedValue === '0' &&
      this.parse(event.target.value) !== 0
    ) {
      event.target.value = event.target.value.replace('0', '');
    }

    if (event.target.value === '') {
      this.previousValue = '';
      this.previousFormattedValue = '';

      return this.args.onInput('', event);
    }

    const parsedValue = this.parse(event.target.value);

    if (Number.isNaN(parsedValue)) {
      event.target.value = this.previousFormattedValue;

      return this.args.onInput(this.previousValue, event);
    }

    this.previousValue = `${parsedValue}`;
    this.previousFormattedValue = event.target.value;

    return this.args.onInput(`${parsedValue}`, event);
  }

  @action
  onBlur(event: Event) {
    assert(
      `Expected input event handler to be used on an element`,
      event.target instanceof HTMLInputElement
    );

    event.target.value = this.format(this.previousValue);
  }

  @action
  onIconRightClick() {
    this.args.onIconRightClick?.();
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Ui::InputGroup::NumberInput': typeof UiInputGroupNumberInputComponent;
    'ui/input-group/number-input': typeof UiInputGroupNumberInputComponent;
  }
}
